import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { 
  Brain, 
  Sparkles, 
  Wand2, 
  Target, 
  TrendingUp, 
  Palette, 
  Clock, 
  Users,
  Plus,
  Play,
  Settings,
  Download,
  Share2,
  TestTube
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/contexts/AuthContext'
import { hasAdminPrivileges } from '@/utils/adminUtils'
import AITestPanel from '@components/AIAgent/AITestPanel'
import APIStatusPanel from '@components/AIAgent/APIStatusPanel'
import OpenAITestPanel from '@components/Debug/OpenAITestPanel'
import AIAPIService from '@/utils/AIAPIService'
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics'

const AIStudio = () => {
  const { currentUserData } = useAuth()
  
  // Admin-only access check
  if (!hasAdminPrivileges(currentUserData)) {
    return <Navigate to="/dashboard" replace />
  }
  
  const [activeTab, setActiveTab] = useState('generate')
  const [generating, setGenerating] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [showTestPanel, setShowTestPanel] = useState(false)
  const [showAPIStatus, setShowAPIStatus] = useState(false)
  const [showOpenAITest, setShowOpenAITest] = useState(false)
  
  // Google Analytics tracking
  const { trackAIFeature, trackFeatureUsage } = useGoogleAnalytics()
  
  // Initialize API service
  const apiService = new AIAPIService()

  const tabs = [
    { id: 'generate', name: 'Content Generation', icon: Wand2 },
    { id: 'optimize', name: 'Performance Optimization', icon: TrendingUp },
    { id: 'design', name: 'Design Assistant', icon: Palette },
    { id: 'schedule', name: 'Smart Scheduling', icon: Clock },
    { id: 'audience', name: 'Audience Analysis', icon: Users }
  ]

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt')
      return
    }

    setGenerating(true)
    
    // Track AI content generation
    trackAIFeature('content_generation', 'started')
    
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 3000))
    setGenerating(false)
    toast.success('Content generated successfully!')
    
    // Track successful AI content generation
    trackAIFeature('content_generation', 'completed')
  }

  const handleTestAI = (testPrompt) => {
    // This will be handled by the AI agent panel
    toast.success(`Testing AI with: "${testPrompt}"`)
    
    // Track AI testing
    trackAIFeature('ai_testing', 'tested')
  }

  const aiFeatures = [
    {
      title: 'Smart Content Generation',
      description: 'Generate engaging content based on your business type and audience',
      icon: Wand2,
      color: 'bg-purple-500'
    },
    {
      title: 'Performance Optimization',
      description: 'AI analyzes your content performance and suggests improvements',
      icon: TrendingUp,
      color: 'bg-green-500'
    },
    {
      title: 'Design Enhancement',
      description: 'Get AI-powered design suggestions for better visual appeal',
      icon: Palette,
      color: 'bg-blue-500'
    },
    {
      title: 'Audience Targeting',
      description: 'Optimize content for specific audience segments and behaviors',
      icon: Target,
      color: 'bg-orange-500'
    }
  ]

  const recentGenerations = [
    {
      id: 1,
      title: 'Welcome Message',
      type: 'text',
      status: 'completed',
      timestamp: '2 minutes ago'
    },
    {
      id: 2,
      title: 'Promotional Banner',
      type: 'image',
      status: 'processing',
      timestamp: '5 minutes ago'
    },
    {
      id: 3,
      title: 'Schedule Optimization',
      type: 'schedule',
      status: 'completed',
      timestamp: '1 hour ago'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <Brain className="w-8 h-8 mr-3" />
              AI Studio
            </h1>
            <p className="text-purple-100">
              Leverage artificial intelligence to create, optimize, and manage your digital signage content
            </p>
          </div>
          <div className="hidden md:flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-medium">AI Powered</span>
          </div>
        </div>
      </div>

      {/* AI Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {aiFeatures.map((feature, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className={`${feature.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
              <feature.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
            <p className="text-sm text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* AI Test Panel Toggle */}
      <div className="flex justify-center space-x-4 flex-wrap">
        <button
          onClick={() => {
            setShowTestPanel(!showTestPanel)
            trackAIFeature('ai_test_panel', showTestPanel ? 'hidden' : 'shown')
          }}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all"
        >
          <Brain className="w-5 h-5 mr-2" />
          {showTestPanel ? 'Hide AI Test Panel' : 'Show AI Test Panel'}
        </button>
        
        <button
          onClick={() => {
            setShowAPIStatus(!showAPIStatus)
            trackAIFeature('api_status_panel', showAPIStatus ? 'hidden' : 'shown')
          }}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-lg hover:from-green-600 hover:to-teal-700 transition-all"
        >
          <Settings className="w-5 h-5 mr-2" />
          {showAPIStatus ? 'Hide API Status' : 'Show API Status'}
        </button>

        <button
          onClick={() => {
            setShowOpenAITest(!showOpenAITest)
            trackAIFeature('openai_test_panel', showOpenAITest ? 'hidden' : 'shown')
          }}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-all"
        >
          <TestTube className="w-5 h-5 mr-2" />
          {showOpenAITest ? 'Hide OpenAI Test' : 'Test OpenAI API'}
        </button>
      </div>

      {/* AI Test Panel */}
      {showTestPanel && (
        <AITestPanel onTestAI={handleTestAI} />
      )}

      {/* API Status Panel */}
      {showAPIStatus && (
        <APIStatusPanel apiService={apiService} />
      )}

      {/* OpenAI Test Panel */}
      {showOpenAITest && (
        <OpenAITestPanel />
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Content Generation */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Content Generation</h2>
              <p className="text-sm text-gray-600 mt-1">Generate engaging content with AI assistance</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Describe your content
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., Create a welcome message for a restaurant with a modern, friendly tone..."
                    className="w-full h-32 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:from-purple-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Generate Content
                      </>
                    )}
                  </button>
                  
                  <button className="flex items-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    <Settings className="w-4 h-4 mr-2" />
                    Advanced
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Generated Content Preview */}
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Generated Content</h3>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-600 text-sm">
                  Your generated content will appear here. Use the form above to create new content with AI assistance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Generations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Recent Generations</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentGenerations.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        {item.type === 'text' && <span className="text-xs font-medium text-primary-600">T</span>}
                        {item.type === 'image' && <span className="text-xs font-medium text-primary-600">I</span>}
                        {item.type === 'schedule' && <span className="text-xs font-medium text-primary-600">S</span>}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-500">{item.timestamp}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {item.status === 'completed' && (
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      )}
                      {item.status === 'processing' && (
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      )}
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Play className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              <button className="w-full flex items-center justify-center px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
                <Plus className="w-4 h-4 mr-2" />
                New Generation
              </button>
              <button className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                <Download className="w-4 h-4 mr-2" />
                Export All
              </button>
              <button className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                <Share2 className="w-4 h-4 mr-2" />
                Share Workspace
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Subscription Upgrade Modal */}
      {/* This modal is no longer needed as access is admin-only */}
    </div>
  )
}

export default AIStudio 